import { createFeatureSelector, createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';
import { PermPostOfferWorkflowState } from './state/perm-post-offer-workflow.state';
import { LoadingTypes } from 'src/app/shared/models/enums/loading.enum';
import { SubmittalRemovalReason } from 'src/app/accountmanagers/submittals/model/submittal-removal-reasons.model';
import { PostOfferInformation } from './models/perm-post-offer-information.model';
import { PermPostOfferWorkflowSubmittalInformationExpanded } from './models/perm-post-offer-workflow-submittal-info.model';
import { SubmittalJobStatus } from 'src/app/shared/models/enums/submittal-job-status.enum';
import { PostOfferWorkflowLoadingStateType } from './models/perm-post-offer-modal-loading-state.model';
import { PostOfferModalMetadata } from './models/post-offer-modal-metadata.model';
import { deserializeFilter, PermCandidatesGridFetchArgs } from './models/perm-post-offer-candidates-grid-filter.model';
import { PermPostOfferCandidatesInformation } from './models/perm-post-offer-candidates-information.model';
import { PagingTokenFixed } from 'src/app/shared/models/core';
import { PermanentCandidateItemModel } from 'src/app/shared/models/permanent-candidate-item.model';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { permPostOfferWorkflowFeatureKey } from './perm-post-offer-workflow.reducer';
import { PermPostOfferLinks } from 'src/app/accountmanagers/perm-post-offer/services/perm-post-offer.service';

export const selectPermPostOfferWorkflowState: MemoizedSelector<
    object,
    PermPostOfferWorkflowState,
    DefaultProjectorFn<PermPostOfferWorkflowState>
> = createFeatureSelector<PermPostOfferWorkflowState>(permPostOfferWorkflowFeatureKey);

export const selectInterviewSubStatuses: MemoizedSelector<
    object,
    Array<KeyValue<number, string>>,
    (state: PermPostOfferWorkflowState) => Array<KeyValue<number, string>>
> = createSelector(selectPermPostOfferWorkflowState, (state) => state.interviewSubStatuses);

export const selectPostOfferModalIsLoading: MemoizedSelector<
    object,
    { isLoading: boolean; isSubmitting: boolean },
    (state: PermPostOfferWorkflowState) => { isLoading: boolean; isSubmitting: boolean }
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    if (!state.postOfferWorkflowLoadingStates || state.postOfferWorkflowLoadingStates.length < 1) {
        return { isLoading: false, isSubmitting: false };
    }

    const isLoading: boolean = state.postOfferWorkflowLoadingStates.some(
        (loadingState) => loadingState.loadingType === LoadingTypes.LOADING
    );

    const isSubmitting: boolean = state.postOfferWorkflowLoadingStates.some(
        (loadingState) =>
            loadingState.loadingStateType === PostOfferWorkflowLoadingStateType.SaveSubmittalStatusAndPostOfferInfo
    );

    return { isLoading, isSubmitting };
});

export const selectPermPostOfferConfirmNotStartingModalIsSubmitting: MemoizedSelector<
    object,
    { isSubmitting: boolean },
    (state: PermPostOfferWorkflowState) => { isSubmitting: boolean }
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    if (!state.postOfferWorkflowLoadingStates || state.postOfferWorkflowLoadingStates.length < 1) {
        return { isSubmitting: false };
    }

    const isLoading: boolean = state.postOfferWorkflowLoadingStates.some(
        (loadingState) => loadingState.loadingType === LoadingTypes.LOADING
    );

    const isSubmitting: boolean = state.postOfferWorkflowLoadingStates.some(
        (loadingState) => loadingState.loadingStateType === PostOfferWorkflowLoadingStateType.SetConfirmNotStarting
    );

    return { isSubmitting: isLoading && isSubmitting };
});

export const selectSubmittalInformation: MemoizedSelector<
    object,
    PermPostOfferWorkflowSubmittalInformationExpanded | undefined,
    (state: PermPostOfferWorkflowState) => PermPostOfferWorkflowSubmittalInformationExpanded | undefined
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    return state.submittalInformation;
});

export const selectFacilityPermBillingInformation: MemoizedSelector<
    object,
    string | undefined,
    (submittalInformation: PermPostOfferWorkflowSubmittalInformationExpanded) => string | undefined
> = createSelector(
    selectSubmittalInformation,
    (submittalInformation: PermPostOfferWorkflowSubmittalInformationExpanded) => {
        return submittalInformation?.permFacilityBillingInformation;
    }
);

export const selectOfferDeclinedRemovalReasons: MemoizedSelector<
    object,
    SubmittalRemovalReason[],
    (state: PermPostOfferWorkflowState) => SubmittalRemovalReason[]
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    return state.candidateDeclinedRemovalReasons;
});

export const selectPostOfferData: MemoizedSelector<
    object,
    PostOfferInformation | undefined,
    (state: PermPostOfferWorkflowState) => PostOfferInformation | undefined
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    if (!state.postOfferInformation) {
        return undefined;
    }

    return {
        jobOfferStatus: state.postOfferInformation.jobOfferStatus,
        billingAmount: state.postOfferInformation.billingAmount,
        startDate: state.postOfferInformation.startDate ? new Date(state.postOfferInformation.startDate) : null,
        offerDate: state.postOfferInformation.offerDate ? new Date(state.postOfferInformation.offerDate) : null
    };
});

export const selectDefaultBillingAmount: MemoizedSelector<
    object,
    number | undefined,
    (state: PermPostOfferWorkflowState) => number | undefined
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    // TODO: replace return line with below when PERM-1992 is fully hashed out. todo: PERM-1992
    //  return state.postOfferInformation?.billingAmount ?? state.submittalInformation?.jobInformation?.placementFee;
    return (
        state.postOfferInformation?.billingAmount ??
        (state.submittalInformation?.jobInformation?.placementFee
            ? state.submittalInformation?.jobInformation?.placementFee
            : state.submittalInformation?.jobInformation?.billRate)
    );
});

export const selectLegacyStatusId: MemoizedSelector<
    object,
    SubmittalJobStatus | undefined,
    (state: PermPostOfferWorkflowState) => SubmittalJobStatus | undefined
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    return state.postOfferLegacyInformation?.legacyStatusId;
});

export const selectPostOfferModelMetadata: MemoizedSelector<
    object,
    PostOfferModalMetadata | undefined,
    (state: PermPostOfferWorkflowState) => PostOfferModalMetadata | undefined
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    return state.postOfferModal;
});

export const selectPostOfferCandidatesInformationState: MemoizedSelector<
    object,
    PermPostOfferCandidatesInformation,
    (state: PermPostOfferWorkflowState) => PermPostOfferCandidatesInformation
> = createSelector(selectPermPostOfferWorkflowState, (state) => state.postOfferCandidatesInformation);

export const selectCandidatesInformationIsLoading: MemoizedSelector<
    object,
    boolean,
    (state: PermPostOfferCandidatesInformation) => boolean
> = createSelector(selectPostOfferCandidatesInformationState, (state) => state.loadingType === LoadingTypes.LOADING);

export const selectPreStartCandidatesInformation: MemoizedSelector<
    object,
    PagingTokenFixed<PermanentCandidateItemModel[]>,
    (state: PermPostOfferCandidatesInformation) => PagingTokenFixed<PermanentCandidateItemModel[]>
> = createSelector(selectPostOfferCandidatesInformationState, (state) =>
    selectPaginatedPermanentCandidatesByGrid(state.preStartCandidates)
);

export const selectPreStartCandidatesInformationArgs: MemoizedSelector<
    object,
    PermCandidatesGridFetchArgs,
    (state: PermPostOfferCandidatesInformation) => PermCandidatesGridFetchArgs
> = createSelector(selectPostOfferCandidatesInformationState, (state) => {
    const result = state.preStartFetchArgs;
    if (result.filter) {
        return {
            ...result,
            filter: deserializeFilter(result.filter)
        };
    }
    return result;
});

export const selectConfirmedCandidatesInformation: MemoizedSelector<
    object,
    PagingTokenFixed<PermanentCandidateItemModel[]>,
    (state: PermPostOfferCandidatesInformation) => PagingTokenFixed<PermanentCandidateItemModel[]>
> = createSelector(selectPostOfferCandidatesInformationState, (state) =>
    selectPaginatedPermanentCandidatesByGrid(state.confirmedCandidates)
);

export const selectConfirmedCandidatesInformationArgs: MemoizedSelector<
    object,
    PermCandidatesGridFetchArgs,
    (s1: PermPostOfferCandidatesInformation) => PermCandidatesGridFetchArgs
> = createSelector(selectPostOfferCandidatesInformationState, (state) => {
    const result = state.confirmedFetchArgs;
    if (result.filter) {
        return {
            ...result,
            filter: deserializeFilter(result.filter)
        };
    }
    return result;
});

export const selectPostOfferBillingUpdateModalIsLoading: MemoizedSelector<
    object,
    { isLoading: boolean },
    (state: PermPostOfferWorkflowState) => { isLoading: boolean }
> = createSelector(selectPermPostOfferWorkflowState, (state: PermPostOfferWorkflowState) => {
    if (!state.postOfferWorkflowLoadingStates || state.postOfferWorkflowLoadingStates.length < 1) {
        return { isLoading: false };
    }

    const isLoading: boolean = state.postOfferWorkflowLoadingStates.some(
        (loadingState) => loadingState.loadingType === LoadingTypes.LOADING
    );
    return { isLoading };
});

const selectPaginatedPermanentCandidatesByGrid = (
    paginatedPermanentCandidates: PagingTokenFixed<PermanentCandidateItemModel[]>
): PagingTokenFixed<PermanentCandidateItemModel[]> => {
    if (paginatedPermanentCandidates) {
        const permCandidateModels = paginatedPermanentCandidates.data.map((item) => ({
            ...item,
            aboutLink: PermPostOfferLinks.CandidateLinkByContractorStatus(item.isSubcontractor, item.candidateId),
            startDateTodayOrPastWarningClass: selectStartDateTodayOrPastWarningClass(item.isStartDateTodayOrPast)
        }));
        return {
            ...paginatedPermanentCandidates,
            data: permCandidateModels
        };
    }
    return paginatedPermanentCandidates;
};

const selectStartDateTodayOrPastWarningClass = (isStartDateTodayOrPast: boolean): string => {
    return isStartDateTodayOrPast ? 'grid-alert' : '';
};
